import type { UserRole } from '@/api/types/user.types';

// Sidebar/route access matrix, agreed with the business owner 2026-07-21. Each key maps to one
// or more real routes (see ROUTE_PREFIX_TO_KEY below) - a role either sees the whole nav
// group/page or doesn't, there's no per-field granularity here.
export type NavKey =
  | 'dashboard'
  | 'intake'
  | 'tickets'
  | 'queue'
  | 'customers'
  | 'technicians'
  | 'packagePickup'
  | 'inventory'
  | 'sales'
  | 'cash'
  | 'invoices'
  | 'payments'
  | 'commissions'
  | 'reports'
  | 'users'
  | 'settings'
  | 'aiDiagnosis'
  | 'aiQuickIntake';

// Longest-prefix-per-entry match against the URL pathname. /ai/diagnosis and /ai/quick-intake
// are listed before any bare '/ai' entry would be (there isn't one) so they resolve to their
// own distinct keys rather than a shared 'ai' bucket - the owner's spec named "אבחון AI"
// specifically for Technician and did not mention quick-intake, so the two need to be gate-able
// independently. A path that matches nothing here (e.g. /profile) is always allowed - only
// explicitly-listed areas are gated.
const ROUTE_PREFIX_TO_KEY: [string, NavKey][] = [
  ['/dashboard', 'dashboard'],
  ['/intake', 'intake'],
  ['/tickets', 'tickets'],
  ['/queue', 'queue'],
  ['/customers', 'customers'],
  ['/technicians', 'technicians'],
  ['/package-pickup', 'packagePickup'],
  ['/inventory', 'inventory'],
  ['/sales', 'sales'],
  ['/cash', 'cash'],
  ['/invoices', 'invoices'],
  ['/payments', 'payments'],
  ['/commissions', 'commissions'],
  ['/reports', 'reports'],
  ['/users', 'users'],
  ['/settings', 'settings'],
  ['/ai/diagnosis', 'aiDiagnosis'],
  ['/ai/quick-intake', 'aiQuickIntake'],
];

// Technician: repair-floor work only - dashboard, tickets/queue, customers (view + basic edit,
// already unrestricted on the backend), parts (view + use, management stays admin/manager), both
// AI tabs (2026-08-19: quick-intake opened up to match diagnosis - her call, both live under one
// "AI" sidebar section anyway). No commerce (מסחר), no reports, no users/settings, no
// technician-roster management.
// Viewer: read-only owner/accountant view of a narrower set - notably includes Reports (which
// Technician does NOT get) but excludes commerce/settings/users entirely (not even read) and
// the technician roster. Actual write-blocking for Viewer is a separate, already-existing
// global mechanism (see ReadOnlyForViewerHandler on the backend) - this matrix only controls
// which pages are visible/reachable at all.
// Admin/Manager: full access (both AI tabs included) - unrestricted, no entry needed here. Manager
// isn't currently used for anyone but the owner's own account, so it deliberately mirrors Admin
// here rather than getting its own restricted matrix - the backend's existing "admin,manager"
// pattern already reserves true admin-only actions (integration secrets, deleting/inviting users,
// etc.) for Admin specifically.
const ROLE_NAV_ACCESS: Partial<Record<UserRole, NavKey[]>> = {
  Technician: ['dashboard', 'intake', 'tickets', 'queue', 'customers', 'packagePickup', 'inventory', 'aiDiagnosis', 'aiQuickIntake'],
  Viewer: ['dashboard', 'tickets', 'queue', 'customers', 'inventory', 'reports'],
};

function matchNavKey(pathname: string): NavKey | undefined {
  const entry = ROUTE_PREFIX_TO_KEY.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  return entry?.[1];
}

// Roles without an explicit entry above (Admin, Manager, and the legacy Sales/Reception/
// FranchiseAdmin roles no longer offered in the create-user form) get unrestricted access -
// unchanged from before this permissions pass.
// KioskIntake is default-closed (only /kiosk), unlike every other role below which is default-open on anything unlisted.
const KIOSK_ROUTE = '/kiosk';

export function hasNavAccess(role: UserRole | undefined, pathname: string): boolean {
  if (!role) return false;
  if (role === 'KioskIntake') return pathname === KIOSK_ROUTE || pathname.startsWith(`${KIOSK_ROUTE}/`);

  const allowed = ROLE_NAV_ACCESS[role];
  if (!allowed) return true;

  const key = matchNavKey(pathname);
  if (!key) return true;

  return allowed.includes(key);
}

export function isNavKeyAllowed(role: UserRole | undefined, key: NavKey): boolean {
  if (!role) return false;
  const allowed = ROLE_NAV_ACCESS[role];
  return !allowed || allowed.includes(key);
}

// Admin/manager-only destructive actions that stay visible in the UI for everyone else today
// (e.g. deleting a customer) but are now blocked on the backend - use this to hide the button
// instead of letting the user hit a confusing 403.
export function isManagerOrAbove(role: UserRole | undefined): boolean {
  return role === 'Admin' || role === 'Manager';
}
